/**
 * Entity Sync Hooks
 *
 * Real-time entity synchronization via Socket.IO.
 * When a user views an entity (e.g. work order), these hooks:
 * - Join/leave the entity room for presence tracking
 * - Listen for entity:changed events and invalidate React Query cache
 * - Track other viewers (presence)
 */

import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { queryKeys } from './queryKeys';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface EntityChangedEvent {
  entityType: string;
  entityId: string;
  scope: string;
  action: string;
  actorId: string;
  actorName: string;
  timestamp: string;
}

export interface EntityViewer {
  userId: string;
  firstName: string;
  /** Sent since #3409; an older server sends only `firstName`. */
  lastName?: string;
  /** "Etunimi Sukunimi", or the whole email when the user has no name. */
  displayName?: string;
}

interface EntityPresenceEvent {
  viewers: EntityViewer[];
}

// ─── Scope → Query Key mapping ──────────────────────────────────────────────

function getInvalidationKeys(entityType: string, entityId: string, scope: string): { queryKey: readonly unknown[] }[] {
  if (entityType === 'workorder') {
    // Billable fields (items, parts, services, time, invoice) change the
    // preview for this work order only — notes do not (#2643).
    const invoicePreview = { queryKey: queryKeys.invoicePreview(entityId) };
    switch (scope) {
      case 'status':
      case 'details':
      case 'services':
        return [
          { queryKey: queryKeys.workOrders.detail(entityId) },
          { queryKey: queryKeys.workOrders.lists() },
          { queryKey: queryKeys.workOrders.myServices() },
          invoicePreview,
        ];
      case 'items':
        return [
          { queryKey: queryKeys.workOrders.detail(entityId) },
          invoicePreview,
        ];
      case 'notes':
        return [
          { queryKey: queryKeys.workOrders.notes(entityId) },
        ];
      case 'parts':
        return [
          { queryKey: queryKeys.workOrders.detail(entityId) },
          invoicePreview,
        ];
      case 'timeEntries':
        return [
          { queryKey: queryKeys.workOrders.detail(entityId) },
          { queryKey: queryKeys.workOrders.timeEntries(entityId) },
          { queryKey: [...queryKeys.bookings.all, 'calendar'] },
          invoicePreview,
        ];
      case 'invoice':
        // The Maksut card reads the invoice itself and the work order's
        // invoice history, not only the preview; voids, credits and payment
        // reversals arrive from the invoice page in another tab (#3117).
        return [
          { queryKey: queryKeys.workOrders.detail(entityId) },
          invoicePreview,
          { queryKey: queryKeys.invoices.all },
          { queryKey: ['workOrderInvoices', entityId] },
        ];
      default:
        return [
          { queryKey: queryKeys.workOrders.detail(entityId) },
          invoicePreview,
        ];
    }
  }

  if (entityType === 'vehicle') {
    switch (scope) {
      case 'details':
        return [
          { queryKey: queryKeys.vehicles.detail(entityId) },
          { queryKey: queryKeys.vehicles.lists() },
          // Registration / make / model are shown on work order cards (#2643).
          { queryKey: queryKeys.workOrders.lists() },
          { queryKey: queryKeys.workOrders.details() },
          { queryKey: queryKeys.workOrders.myServices() },
        ];
      case 'owner':
      case 'holder':
        return [
          { queryKey: queryKeys.vehicles.detail(entityId) },
        ];
      default:
        return [
          { queryKey: queryKeys.vehicles.detail(entityId) },
        ];
    }
  }

  /**
   * `person` (#2100 Gate 1). Until this branch existed, `getInvalidationKeys`
   * handled only `workorder` and `vehicle`, so a `person` event invalidated
   * NOTHING — and no persons page subscribed to the collection room either.
   * The portal assistant looked like it worked only because
   * `useAssistantChat.confirmAction` invalidates `queryKeys.persons.all`
   * itself: the chat tab refreshing itself, not the event arriving.
   */
  if (entityType === 'person') {
    switch (scope) {
      case 'details':
        return [
          { queryKey: queryKeys.persons.detail(entityId) },
          { queryKey: queryKeys.persons.lists() },
        ];
      default:
        return [
          { queryKey: queryKeys.persons.detail(entityId) },
          { queryKey: queryKeys.persons.lists() },
        ];
    }
  }

  return [];
}

// ─── Singleton socket per tab ───────────────────────────────────────────────

let sharedSocket: Socket | null = null;
let socketRefCount = 0;
/** True only after we called disconnect() on the shared socket (last unmount). */
let sharedSocketClosedByUs = false;

/**
 * Reuse the live manager, including while it is reconnecting.
 * socket.io-client v4: `active` stays true during automatic reconnection
 * (`disconnected` is true in that window) and is false after an explicit
 * client `disconnect()` or when the manager has fully closed.
 * Test doubles may omit `active`; treat a missing value as reusable.
 */
function isSharedSocketReusable(socket: Socket): boolean {
  return !sharedSocketClosedByUs && socket.active !== false;
}

function getSharedSocket(token: string): Socket {
  if (!sharedSocket || !isSharedSocketReusable(sharedSocket)) {
    sharedSocket = io('/', {
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
      transports: ['websocket', 'polling'],
    });
    sharedSocketClosedByUs = false;
  }
  socketRefCount++;
  return sharedSocket;
}

function releaseSharedSocket(): void {
  socketRefCount--;
  if (socketRefCount <= 0 && sharedSocket) {
    sharedSocketClosedByUs = true;
    sharedSocket.disconnect();
    sharedSocket = null;
    socketRefCount = 0;
  }
}

// ─── Hook: useEntityRoom ────────────────────────────────────────────────────

/**
 * Joins/leaves an entity room. Re-joins on reconnect.
 * Returns the socket instance for other hooks to use.
 */
export function useEntityRoom(entityType: string | undefined, entityId: string | undefined) {
  const token = useAuthStore((s) => s.accessToken);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!token || !entityType || !entityId) return;

    const socket = getSharedSocket(token);
    socketRef.current = socket;

    const joinPayload = { entityType, entityId };

    const joinRoom = () => {
      socket.emit('entity:join', joinPayload);
    };

    // Join immediately if connected, otherwise wait
    if (socket.connected) {
      joinRoom();
    }
    socket.on('connect', joinRoom);

    return () => {
      socket.off('connect', joinRoom);
      socket.emit('entity:leave', joinPayload);
      socketRef.current = null;
      releaseSharedSocket();
    };
  }, [token, entityType, entityId]);

  return socketRef;
}

// ─── Hook: useEntityChanged ─────────────────────────────────────────────────

/**
 * Listens for entity:changed events and invalidates the relevant React Query cache.
 * Cache is always invalidated (supports same user in multiple tabs).
 * lastChange is only set for OTHER users' mutations (to show toast).
 */
export function useEntityChanged(
  entityType: string | undefined,
  entityId: string | undefined,
) {
  const userId = useAuthStore((s) => s.user?.id);
  const queryClient = useQueryClient();
  const [lastChange, setLastChange] = useState<EntityChangedEvent | null>(null);

  useEffect(() => {
    if (!entityType || !entityId) return;
    const socket = sharedSocket;
    if (!socket) return;

    const handler = (event: EntityChangedEvent) => {
      // Only handle events for this entity
      if (event.entityType !== entityType || event.entityId !== entityId) return;

      // Always invalidate cache (covers same user in different tab)
      const keys = getInvalidationKeys(entityType, entityId, event.scope);
      for (const key of keys) {
        void queryClient.invalidateQueries(key);
      }

      // Only show notification for OTHER users' changes
      if (event.actorId !== userId) {
        setLastChange(event);
      }
    };

    socket.on('entity:changed', handler);

    // On reconnect, force invalidate to catch missed events
    const reconnectHandler = () => {
      const keys = getInvalidationKeys(entityType, entityId, 'status');
      for (const key of keys) {
        void queryClient.invalidateQueries(key);
      }
    };
    socket.on('connect', reconnectHandler);

    return () => {
      socket.off('entity:changed', handler);
      socket.off('connect', reconnectHandler);
      setLastChange(null);
    };
  }, [entityType, entityId, userId, queryClient]);

  return lastChange;
}

// ─── Hook: useEntityPresence ────────────────────────────────────────────────

/**
 * Tracks who else is viewing the same entity.
 * Filters out the current user from the viewer list.
 */
export function useEntityPresence(entityType: string | undefined, entityId: string | undefined) {
  const userId = useAuthStore((s) => s.user?.id);
  const [viewers, setViewers] = useState<EntityViewer[]>([]);

  useEffect(() => {
    if (!entityType || !entityId) return;
    const socket = sharedSocket;
    if (!socket) return;

    const handler = (data: EntityPresenceEvent) => {
      // Filter out current user
      const others = data.viewers.filter((v) => v.userId !== userId);
      setViewers(others);
    };

    socket.on('entity:presence', handler);

    return () => {
      socket.off('entity:presence', handler);
      setViewers([]);
    };
  }, [entityType, entityId, userId]);

  return viewers;
}

// ─── Combined convenience hook ──────────────────────────────────────────────

/**
 * All-in-one entity sync hook.
 * Joins the room, listens for changes, and tracks presence.
 */
export function useEntitySync(entityType: string | undefined, entityId: string | undefined) {
  useEntityRoom(entityType, entityId);
  const lastChange = useEntityChanged(entityType, entityId);
  const viewers = useEntityPresence(entityType, entityId);
  return { viewers, lastChange };
}

// ─── Hook: useCollectionSync ─────────────────────────────────────────────────

/**
 * Subscribes to a collection room for real-time list/calendar updates.
 * When any entity of the given type is mutated, invalidates the specified queries.
 *
 * Unlike useEntityRoom (per-entity), this subscribes to ALL changes
 * for an entity type within the tenant — ideal for list and calendar views.
 */
export function useCollectionSync(
  entityType: string,
  options?: {
    relevantScopes?: string[];
    invalidationKeys?: { queryKey: readonly unknown[] }[];
  },
): void {
  const token = useAuthStore((s) => s.accessToken);
  const queryClient = useQueryClient();

  // Store options in ref to avoid re-running effect on every render
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!token) return;

    const socket = getSharedSocket(token);

    const subscribePayload = { entityType };

    const subscribe = () => {
      socket.emit('entity:subscribe', subscribePayload);
    };

    // Subscribe immediately if connected, otherwise wait
    if (socket.connected) {
      subscribe();
    }
    socket.on('connect', subscribe);

    const handler = (event: EntityChangedEvent) => {
      const opts = optionsRef.current;

      // Filter by relevant scopes if specified
      if (opts?.relevantScopes && !opts.relevantScopes.includes(event.scope)) {
        return;
      }

      // Invalidate all specified query keys
      if (opts?.invalidationKeys) {
        for (const key of opts.invalidationKeys) {
          void queryClient.invalidateQueries(key);
        }
      }
    };

    socket.on('entity:changed', handler);

    // On reconnect, invalidate all to catch missed events
    const reconnectHandler = () => {
      const opts = optionsRef.current;
      if (opts?.invalidationKeys) {
        for (const key of opts.invalidationKeys) {
          void queryClient.invalidateQueries(key);
        }
      }
    };
    socket.on('connect', reconnectHandler);

    return () => {
      socket.off('connect', subscribe);
      socket.off('entity:changed', handler);
      socket.off('connect', reconnectHandler);
      socket.emit('entity:unsubscribe', subscribePayload);
      releaseSharedSocket();
    };
  }, [token, entityType, queryClient]);
}
